import React, { type CSSProperties } from "react";
import { useRouter } from "@tanstack/react-router";
import { flexRender, type Column, type Table as TableType } from "@tanstack/react-table";

import { Skeleton } from "@/components/ui/skeleton";

import { cn } from "@/lib/utils";
import { useTableNavigation } from "@/hooks/use-table-navigation";

import { DataTablePagination } from "./data-table-pagination";
import { DataTableColumnResizer } from "./data-table-resizer";
import type { DataTableToolbarRef } from "./data-table-toolbar";

interface DataTableProps<T> {
  table: TableType<T>;
  totalCount?: number;
  className?: string;
  isLoading?: boolean;
  getRowUrl?: (row: T) => string | undefined;
  getRowClassName?: (row: T) => string | undefined;
  selectedRowId?: string;
  scope: string;
  data?: T[];
  toolbarRef?: React.RefObject<DataTableToolbarRef | null>;
}

const getCommonPinningStyles = <T,>(column: Column<T>): CSSProperties => {
  const isPinned = column.getIsPinned();
  const isLastLeftPinnedColumn = isPinned === "left" && column.getIsLastColumn("left");
  const isFirstRightPinnedColumn = isPinned === "right" && column.getIsFirstColumn("right");

  return {
    boxShadow: isLastLeftPinnedColumn
      ? "-4px 0 4px -4px var(--border) inset"
      : isFirstRightPinnedColumn
        ? "4px 0 4px -4px var(--border) inset"
        : undefined,
    left: isPinned === "left" ? `${column.getStart("left")}px` : undefined,
    right: isPinned === "right" ? `${column.getAfter("right")}px` : undefined,
    position: isPinned ? "sticky" : "relative",
    width: column.getSize(),
    zIndex: isPinned ? 1 : 0,
    backgroundColor: isPinned ? "var(--background)" : undefined,
    boxSizing: "border-box",
  };
};

export function DataTable<T>({
  ref,
  table,
  totalCount = 0,
  className,
  getRowUrl,
  getRowClassName,
  isLoading,
  selectedRowId,
  scope,
  data: _data,  
  toolbarRef,
}: DataTableProps<T> & { ref?: React.Ref<HTMLTableElement | null> }) {
  const router = useRouter();

  // Helper function to handle navigation with cmd+click support
  const handleNavigation = (row: T, event?: React.MouseEvent) => {
    if (getRowUrl) {
      const url = getRowUrl(row);
      if (url) {
        if (event?.metaKey || event?.ctrlKey) {
          // Cmd+Click (Mac) or Ctrl+Click (Windows/Linux) - open in new tab
          window.open(url, "_blank");
        } else {
          if (url.startsWith("?")) {
            // Query-string only URL - merge with existing search params
            const searchParams = new URLSearchParams(url.slice(1));
            const newParams = new Map<string, string | string[]>();

            // Collect all values for each parameter
            searchParams.forEach((_, key) => {
              const values = searchParams.getAll(key);
              newParams.set(key, values.length === 1 ? values[0] : values);
            });

            void router.navigate({
              to: ".",
              search: (prev: Record<string, unknown>) => ({ ...prev, ...Object.fromEntries(newParams) }),
            });
          } else if (url.includes("?")) {
            const [path, qs] = url.split("?");
            const params = Object.fromEntries(new URLSearchParams(qs).entries());
            void router.navigate({ to: path, search: params });
          } else {
            void router.navigate({ to: url });
          }
        }
      }
    }
  };

  const rows = table.getRowModel().rows;

  const { tableRef, focusedRowIndex, handleRowHover } = useTableNavigation({
    scope,
    rowCount: rows.length,
    onRowSelect: (rowIndex) => {
      const row = rows.at(rowIndex);
      if (row) {
        handleNavigation(row.original);
      }
    },
    canGoPreviousPage: table.getCanPreviousPage(),
    canGoNextPage: table.getCanNextPage(),
    onPreviousPage: () => {
      table.previousPage();
    },
    onNextPage: () => {
      table.nextPage();
    },
    toolbarRef,
  });

  // Forward the table ref to the external ref
  React.useImperativeHandle(ref, () => tableRef.current ?? ({} as HTMLTableElement), [tableRef]);

  return (
    <div
      className={cn("max-h-full overflow-auto rounded-md border bg-background", className)}
      style={{ scrollbarGutter: "stable" }}
    >
      <div className="relative w-full">
        <table
          ref={tableRef}
          className="min-w-full caption-bottom border-separate border-spacing-0 text-sm"
          style={{ width: table.getTotalSize(), tableLayout: "fixed" }}
        >
          <thead className="sticky top-0 z-10 bg-background">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <th
                      key={header.id}
                      className="relative border-b p-0"
                      colSpan={header.colSpan}
                      style={{
                        ...getCommonPinningStyles(header.column),
                        maxWidth: header.getSize(),
                        zIndex: header.column.getIsPinned() ? 20 : 10,
                      }}
                    >
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      <DataTableColumnResizer header={header} />
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 10 }, (_, index) => `skeleton-row-${index}`).map((key) => (

                <tr key={key} className="group">
                  {table.getVisibleLeafColumns().map((column) => (
                    <td
                      key={column.id}
                      className="border-b p-0 px-3 py-[18px] group-last-of-type:border-b-0"
                      style={{
                        ...getCommonPinningStyles(column),
                        maxWidth: column.getSize(),
                      }}
                    >
                      <Skeleton className="h-4 w-4/5" />
                    </td>
                  ))}
                </tr>
              ))
            ) : rows.length ? (
              rows.map((row, index) => {
                const isSelected = selectedRowId === row.id;

                return (
                  <tr
                    key={row.id}
                    data-selected={row.getIsSelected()}
                    data-row-index={index}
                    className={cn(
                      "group scroll-mb-12 transition-colors hover:bg-table-row-hover",
                      focusedRowIndex === index && "bg-table-row-hover",
                      isSelected && "bg-table-row-hover",
                      getRowUrl?.(row.original) && "cursor-pointer",
                      getRowClassName?.(row.original),
                    )}
                    onMouseEnter={() => {
                      handleRowHover(index);
                    }}
                  >
                    {row.getVisibleCells().map((cell) => {
                      const isEditable = !!cell.column.columnDef.meta?.editable;

                      return (
                        <td
                          className={cn("border-b p-0 px-3 py-4 group-last-of-type:border-b-0", {
                            "cursor-pointer":
                              !cell.column.columnDef.meta?.disablePreview && !isEditable && getRowUrl?.(row.original),
                          })}
                          key={cell.id}
                          onClick={(event) => {
                            if (cell.column.columnDef.meta?.disablePreview || isEditable) return;
                            handleNavigation(row.original, event);
                          }}
                          style={{
                            ...getCommonPinningStyles(cell.column),
                            maxWidth: cell.column.getSize(),
                          }}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={table.getVisibleLeafColumns().length} className="h-24 text-center">
                  No results.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="sticky right-0 bottom-0 left-0 z-10 border-t bg-background px-4 py-2">
        <DataTablePagination table={table} totalCount={totalCount} />
      </div>
    </div>
  );
}
